import React from 'react';
import PropTypes from 'prop-types';
import { TextField, styled, Tooltip, Box } from '@mui/material';
import classnames from 'classnames';
import { InputAdornment, InputLabel } from '@material-ui/core';

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& > div': {
    zIndex: 2,
    borderRadius: 12,
    position: 'relative',
    fontSize: '1.125rem',
    lineHeight: '24px',
    fontWeight: 'normal',
    backgroundColor: theme.palette.common.white,
    height: 56,
    overflow: 'hidden',

    'input:-webkit-autofill': {
      boxShadow: 'inset 0 0 0 1000px #fff'
    },

    '&.Mui-error': {
      borderColor: theme.palette.error.main,
      background: theme.palette.error.light
    },

    '&.Mui-disabled': {
      background: theme.palette.action.disabledBackground,

      '& > input': {
        '-webkit-text-fill-color': theme.palette.grey[1000]
      }
    },

    '& > input': {
      padding: '16px 16px',

      '&::placeholder': {
        color: theme.palette.grey[1000],
        opacity: 1
      }
    },

    '& > input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    },
    '& >input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    },

    '& > fieldset': {
      border: `0.5px solid ${theme.palette.action.disabledBackground}`
    },

    '&:hover': {
      '& > fieldset.MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.text.secondary
      }
    },

    '&:hover.Mui-disabled': {
      '& > fieldset': {
        borderColor: theme.palette.action.disabled
      }
    },

    '&.Mui-focused': {
      '& > fieldset.MuiOutlinedInput-notchedOutline': {
        borderWidth: '0.5px',
        borderColor: theme.palette.primary.main
      }
    },

    '&.Mui-focused.Mui-error': {
      '& > fieldset.MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.error.main
      }
    },

    '&.MuiInputBase-sizeSmall': {
      height: 36,

      '& > input': {
        padding: '6px 12px'
      }
    },

    '& > div.MuiInputAdornment-root': {
      '& + input': {
        paddingLeft: 0
      }
    },
  },

  '& > .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: '1rem',
    lineHeight: '24px'
  },

  '.fixed-placeholder': {
    color: theme.palette.text.primary,
  },
}));

export const CustomInput = ({ value, className, required, tooltip, disabled, fixedPlaceholderText = '', ...props }) => {
  const StyledTextFieldToReturn =
    <StyledTextField
      {...props}
      className={classnames(className, required && 'required')}
      disabled={disabled}
      value={value || ''}
      InputProps={{
        // eslint-disable-next-line react/prop-types
        ...props.InputProps,
        startAdornment: fixedPlaceholderText ?
          (
            <InputAdornment position="start">
              <InputLabel className="fixed-placeholder">{fixedPlaceholderText}</InputLabel>
            </InputAdornment>
          ) :
          // eslint-disable-next-line react/prop-types
          props.InputProps?.startAdornment,
      }}
    />;

  return tooltip && disabled ?
    <Tooltip title={tooltip} arrow sx={{pointerEvents: 'auto'}}>
      <Box component="span" data-testid="Wrapper">{StyledTextFieldToReturn}</Box>
    </Tooltip> :
    StyledTextFieldToReturn;

};

CustomInput.propTypes = {
  value: PropTypes.any,
  className: PropTypes.object,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  fixedPlaceholderText: PropTypes.string,
};

CustomInput.defaultProps = {
  value: undefined,
  required: false
};
